import { useMemo, useRef, useState } from 'react';
import { Box, ClickAwayListener, InputBase, Paper, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import { useNavigate } from 'react-router-dom';
import { searchConfig } from './configRegistry';
import { C, RADIUS, SHADOW, TYPE } from './tokens';

/**
 * Type what you're looking for, land on the screen that owns it.
 *
 * Previously the only way to find a setting was to already know which of the
 * seven config surfaces owned it. Search over the registry replaces that with
 * "type 'cut off' -> Pickup cut-off time, Fulfilment".
 *
 * @param merchantId  when known, panel results deep-link straight into that
 *                    merchant. Without it, panel results explain that a merchant
 *                    has to be picked first (and route results still work).
 */
export default function ConfigSearch({ merchantId = null, placeholder = 'e.g. cut off, COD cycle, blocked' }) {
    const navigate = useNavigate();
    const [q, setQ] = useState('');
    const [open, setOpen] = useState(false);
    const [cursor, setCursor] = useState(0);
    const inputRef = useRef(null);

    const results = useMemo(() => searchConfig(q), [q]);

    const go = (item) => {
        if (!item) return;
        if (item.where.kind === 'route') {
            navigate(item.where.url);
        } else if (merchantId) {
            navigate(`/merchant-config/merchant/${merchantId}?tab=${item.where.index}`);
        } else {
            // Nothing to deep-link into yet -- send them to the list to pick one.
            navigate('/merchant-config/merchants');
        }
        setOpen(false);
        setQ('');
    };

    const onKeyDown = (e) => {
        if (!results.length) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => (c + 1) % results.length); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => (c - 1 + results.length) % results.length); }
        else if (e.key === 'Enter') { e.preventDefault(); go(results[cursor]); }
        else if (e.key === 'Escape') { setOpen(false); }
    };

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box sx={{ position: 'relative', width: '100%' }}>
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 1.25, height: 38,
                    bgcolor: C.surface,
                    border: `1px solid ${open ? C.accent : C.line}`,
                    borderRadius: RADIUS.sm,
                    transition: 'border-color .15s',
                }}>
                    <SearchIcon sx={{ fontSize: 17, color: open ? C.accent : C.inkGhost }} />
                    <InputBase
                        inputRef={inputRef}
                        value={q}
                        placeholder={placeholder}
                        onChange={(e) => { setQ(e.target.value); setOpen(true); setCursor(0); }}
                        onFocus={() => setOpen(true)}
                        onKeyDown={onKeyDown}
                        sx={{ flex: 1, fontSize: 13, '& input': { p: 0 }, '& input::placeholder': { color: C.inkGhost, opacity: 1 } }}
                    />
                </Box>

                {open && q.trim().length >= 2 && (
                    <Paper elevation={0} sx={{
                        position: 'absolute', zIndex: 20, top: 'calc(100% + 4px)', left: 0,
                        width: 'max(100%, 380px)',
                        border: `1px solid ${C.line}`, borderRadius: RADIUS.md,
                        boxShadow: SHADOW.pop, overflow: 'hidden', maxHeight: 340, overflowY: 'auto',
                    }}>
                        {results.length === 0 ? (
                            <Box sx={{ px: 1.75, py: 1.75 }}>
                                <Typography sx={TYPE.rowHelp}>
                                    No setting matches “{q}”. Try a word from the label, like “COD”, “pincode” or “weight”.
                                </Typography>
                            </Box>
                        ) : results.map((item, i) => (
                            <Box
                                key={item.key}
                                onMouseEnter={() => setCursor(i)}
                                onClick={() => go(item)}
                                sx={{
                                    px: 1.75, py: 1.1, cursor: 'pointer',
                                    bgcolor: i === cursor ? C.accentSoft : 'transparent',
                                    borderBottom: `1px solid ${C.lineSoft}`,
                                    '&:last-of-type': { borderBottom: 'none' },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                    <Typography sx={{ ...TYPE.rowLabel, flex: 1, minWidth: 0 }}>{item.label}</Typography>
                                    <Typography sx={{ ...TYPE.meta, flexShrink: 0 }}>{item.group}</Typography>
                                    <NorthEastIcon sx={{ fontSize: 12, color: C.inkGhost, flexShrink: 0 }} />
                                </Box>
                                <Typography sx={{ ...TYPE.rowHelp, mt: 0.2 }}>{item.help}</Typography>
                                <Typography sx={{ ...TYPE.meta, mt: 0.3, color: C.accent }}>
                                    {item.where.label}
                                    {item.where.kind === 'panel' && !merchantId && ' — pick a merchant first'}
                                </Typography>
                            </Box>
                        ))}
                    </Paper>
                )}
            </Box>
        </ClickAwayListener>
    );
}
